<template>
    <div :key="order.code" class="bg-white rounded overflow-hidden relative">
        <div class="absolute px-5 pt-5">
            <OrderTypeBadge :type="order.type" light/>
        </div>
        <div class="d-flex flex-column flex-md-row rounded mt-md-n2 px-5 pb-2">
            <center vertical>
                <div class="d-flex flex-column flex-md-row">
                    <div class="w-300px">
                        <table style="line-height: 1.4em;">
                            <tr>
                                <td class="font-weight-bold pr-2">{{ __('general.channel') }}:</td>
                                <td>{{ order.integration.name }}</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold pr-2">{{ __('general.order_nr') }}:</td>
                                <td v-if="order.integration.code === IntegrationCodeEnum.AmazonTODAY">{{ order.meta?.amazon_order_id }}</td>
                                <td v-else>{{ order.integration_order_id }}</td>
                            </tr>
                            <tr>
                                <td class="font-weight-bold pr-2">{{ __('general.date') }}:</td>
                                <td>
                                    <date-time :timestamp="order.created_at"/>
                                </td>
                            </tr>
                            <tr v-if="showPaymentDetails">
                                <td class="font-weight-bold pr-2">{{ __('general.payment') }}:</td>
                                <td>
                                    <div v-if="!order.is_paid" class="text-warning font-weight-bold">
                                        {{ __('orders.unpaid') }}
                                        <i class="fas fa-exclamation-triangle"/>
                                    </div>
                                    <div v-else>
                                        {{ __('orders.paid') }}
                                        <i class="fas fa-check-circle text-muted"/>
                                    </div>
                                </td>
                            </tr>
                        </table>
                    </div>
                    <Center v-if="showCustomerDetails" vertical class="px-3 w-300px font-size-lg">
                        <div class="d-flex flex-row gap-1">
                            <div>
                                <i class="fas fa-id-card mr-1"/>
                            </div>
                            <div>
                                <span class="font-weight-bold">
                                    {{ order.addresses.length > 0 ? order.addresses[0].name : 'Unbekannt' }}
                                </span>
                                <div v-if="order.addresses.length > 0">
                                    {{ order.addresses[0].street1 }},<br>
                                    {{ order.addresses[0].postcode }} {{ order.addresses[0].city }}
                                </div>
                            </div>
                        </div>
                    </Center>
                </div>
            </center>
            <center vertical class="user-select-none w-250px p-5">
                <center class="gap-3 rounded p-4 pt-5" :style="`background-color: ${order.type_color}11;`">
                    <div v-for="item in order.items.slice(0,2)" style="position: relative;">
                        <img class="thumb rounded" :src="item.image_url" :alt="item.title">
                        <div class="px-3 py-1 rounded bg-primary text-white font-weight-bold"
                             style="position: absolute; top: -5px; right: -5px; z-index: 5; font-size: .87em;">
                            {{ item.quantity }}x
                        </div>
                    </div>
                    <div v-if="order.items.length > 2" style="position: relative;">
                        <center vertical class="bg-light-primary thumb rounded">
                            <div class="font-weight-bold text-center text-lg text-primary">
                                +{{ order.items.length - 2 }}
                            </div>
                        </center>
                    </div>
                </center>
            </center>
            <Center v-if="showPackageDetails" vertical class="pt-1 ml-5">
                <div class="d-flex flex-row gap-1">
                    <div class="text-md-left text-center">
                        <div class="font-weight-bold text-muted text-uppercase">
                            Packages
                        </div>
                        <div class="d-flex flex-column flex-xl-row gap-1">
                            <div class="d-none d-xl-flex flex-column justify-content-center bg-light-light rounded-sm py-3 px-5">
                                <i class="fas fa-boxes text-2xl text-muted" />
                            </div>
                            <div v-for="shippingDetails in order.shipping_details"
                                 class="rounded-sm py-3 px-4"
                                 :class="{
                                        'bg-light-success text-success': shippingDetails.package_send,
                                        'bg-light-warning text-warning': shippingDetails.package_prepared && !shippingDetails.package_send,
                                        'bg-light-danger text-danger': !shippingDetails.package_prepared
                                    }"
                            >
                                <div class="font-size-sm text-nowrap">
                                    <div v-if="!shippingDetails.package_prepared">
                                        <!-- <i class="fas fa-exclamation-triangle"/>-->
                                        Unpacked
                                    </div>
                                    <div v-else-if="shippingDetails.package_prepared && !shippingDetails.package_send">
                                        <!-- <i class="fas fa-clock"/>-->
                                        Waiting for pickup
                                    </div>
                                    <div v-else-if="shippingDetails.package_send">
                                        <!-- <i class="fas fa-check-circle"/>-->
                                        Collected
                                    </div>
                                </div>
                                <div class="text-nowrap text-uppercase font-weight-bold">
                                    {{ shippingDetails.package_number }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Center>
            <div class="flex-grow-1 d-flex flex-row justify-content-end py-5">
                <center class="flex-row flex-md-column">
                    <div class="text-right d-flex flex-column">
                        <div class="badge badge-secondary text-muted">
                            {{ __('orders.states.' + order.state) }}
                        </div>
                        <order-deadline-countdown class="mt-1" :key="order.code" :order="order"/>
                        <Link :href="$route('order.details', [store.id, order.url_key])"
                              as="button"
                              :only="['order','users']"
                              class="btn btn-outline-primary px-5 mt-1">
                            {{ __('actions.edit_thing', {thing: __('general.order')}) }}
                            <i class="fas fa-chevron-circle-right ml-1"/>
                        </Link>
                    </div>
                </center>
            </div>
        </div>
    </div>
</template>

<script>
import OrderTypeBadge from './OrderTypeBadge.vue';
import DateTime from '@components/DateTime.vue';
import OrderDeadlineCountdown from './OrderDeadlineCountdown.vue';
import Center from '@components/Layout/Center.vue';
import IntegrationIcon from '@components/Integrations/IntegrationIcon.vue';
import { Link } from '@inertiajs/vue2';
import {IntegrationCodeEnum} from "@generated/integrationCodeEnum";
import Badge from "../Badge.vue";

export default {
    components: {Badge, IntegrationIcon, Center, OrderDeadlineCountdown, DateTime, OrderTypeBadge, Link},

    props: {
        order: Object,
        store: Object,
    },

    computed: {
        IntegrationCodeEnum() {
            return IntegrationCodeEnum
        },

        showPaymentDetails() {
            return this.order.integration.code !== IntegrationCodeEnum.AmazonTODAY
        },

        showPackageDetails() {
            return this.order.integration.code === IntegrationCodeEnum.AmazonTODAY
        },

        showCustomerDetails() {
            return this.order.integration.code !== IntegrationCodeEnum.AmazonTODAY
        }
    }
};
</script>

<style scoped>
.thumb {
    height: 52px;
    width: 52px;
    object-fit: cover;
}
</style>
